import { Button, ExpandablePanel, ToolTip, Card } from "@truevault/aquilo";
import React, { useCallback, useState } from "react";
import {
  PrivacyCenterNoticeDto,
  PrivacyCenterPolicySectionDto,
} from "../../../../../common/service/server/dto/PrivacyCenterDto";
import { FinancialIncentiveNoticeEditor } from "./FinancialIncentiveNoticeEditor";
import { BiometricIdentifiersEditor } from "./BiometricIdentifiersEditor";
import { StateNoticesStep } from "./USStateNoticesPage";
import { Form } from "react-final-form";
import { useMutation } from "@tanstack/react-query";
import { Api } from "../../../../../common/service/Api";
import {
  cleanStatePrivacyNoticeName,
  downloadStateSectionContent,
  RestoreStateSectionWarningModal,
  USStateNoticeEditor,
} from "./USStateNoticeEditor";
import { EyeSlashIcon, LockOpenIcon } from "@heroicons/react/24/solid";
import { useSnackBar } from "@truevault/aquilo";
import { ExternalLink } from "../../../../../common/components/ExternalLink";

function useUpdateStateSectionVisibilityApi() {
  const { mutateAsync: updateStateSectionVisibility, ...rest } = useMutation(
    ({
      organizationId,
      privacyCenterId,
      noticeId,
      stateSectionAnchor,
    }: {
      organizationId: string;
      privacyCenterId: string;
      noticeId: string;
      stateSectionAnchor: string;
    }) =>
      Api.notice.updateStateSectionVisibility(
        organizationId,
        privacyCenterId,
        noticeId,
        stateSectionAnchor,
      ),
  );

  return { updateStateSectionVisibility, ...rest };
}

function useCreateEditableStateSectionApi() {
  const { mutateAsync: createEditableStateSection, ...rest } = useMutation(
    ({
      organizationId,
      privacyCenterId,
      noticeId,
      stateSectionAnchor,
    }: {
      organizationId: string;
      privacyCenterId: string;
      noticeId: string;
      stateSectionAnchor: string;
    }) =>
      Api.notice.createEditableStateSection(
        organizationId,
        privacyCenterId,
        noticeId,
        stateSectionAnchor,
      ),
  );

  return { createEditableStateSection, ...rest };
}

function useRemoveEditableStateSectionApi() {
  const { mutateAsync: removeEditableStateSection, ...rest } = useMutation(
    ({
      organizationId,
      privacyCenterId,
      noticeId,
      stateSectionAnchor,
    }: {
      organizationId: string;
      privacyCenterId: string;
      noticeId: string;
      stateSectionAnchor: string;
    }) =>
      Api.notice.removeEditableStateSection(
        organizationId,
        privacyCenterId,
        noticeId,
        stateSectionAnchor,
      ),
  );

  return { removeEditableStateSection, ...rest };
}

export function USStateNoticesEditor({
  orgId,
  privacyCenterId,
  draftNotice,
  refreshNotice,
  stateSections,
  currentStep,
  setCurrentStep,
  offersFinancialIncentives,
  isFinancialIncentiveSectionFinished,
  isRetentionPolicySectionFinished,
  collectsConsumerBiometricData,
  isBiometricIdentifiersSectionFinished,
  setIsSaved,
  setIsSaving,
}: {
  orgId: string;
  privacyCenterId: string;
  refreshNotice: () => Promise<void>;
  draftNotice: PrivacyCenterNoticeDto;
  stateSections: PrivacyCenterPolicySectionDto[];
  currentStep: StateNoticesStep;
  setCurrentStep: (step: StateNoticesStep) => void;
  offersFinancialIncentives: boolean;
  isFinancialIncentiveSectionFinished: boolean;
  isRetentionPolicySectionFinished: boolean;
  collectsConsumerBiometricData: boolean;
  isBiometricIdentifiersSectionFinished: boolean;
  setIsSaved: (isSaved: boolean) => void;
  setIsSaving: (isSaving: boolean) => void;
}) {
  const enqueueSnackbar = useSnackBar();

  const { updateStateSectionVisibility } = useUpdateStateSectionVisibilityApi();

  const { createEditableStateSection } = useCreateEditableStateSectionApi();
  const { removeEditableStateSection } = useRemoveEditableStateSectionApi();

  const [selectedStateSectionAnchor, setSelectedStateSectionAnchor] = useState<string | null>(null);

  const hasFinancialIncentiveSection =
    draftNotice?.sections?.find((s) => s.anchor === "notice-of-financial-incentive") !== undefined;
  const hasCustomRetentionPolicySection =
    draftNotice?.sections?.find((s) => s.anchor === "data-retention-policy") !== undefined;
  const hasBiometricIdentifiersSection =
    draftNotice?.sections?.find((s) => s.anchor === "colorado-biometric-identifiers-policy") !==
    undefined;

  const isCaliforniaSectionVisible = !(draftNotice?.hiddenStateSectionAnchors || []).includes(
    "california-privacy-notice",
  );
  const isColoradoSectionVisible = !(draftNotice?.hiddenStateSectionAnchors || []).includes(
    "colorado-privacy-notice",
  );

  const restoreStateSection = useCallback(
    async (stateAnchor: string, sectionName: string) => {
      await removeEditableStateSection({
        organizationId: orgId,
        privacyCenterId: privacyCenterId,
        noticeId: draftNotice.publicId,
        stateSectionAnchor: stateAnchor,
      });
      await refreshNotice();

      enqueueSnackbar(
        `TrueVault's ${cleanStatePrivacyNoticeName(
          sectionName,
        )} Privacy Notice was successfully restored.`,
        { variant: "success" },
      );
    },
    [
      removeEditableStateSection,
      orgId,
      privacyCenterId,
      draftNotice,
      refreshNotice,
      enqueueSnackbar,
    ],
  );

  return (
    <div>
      <div style={{ display: "flex", marginTop: "24px", gap: "62px" }}>
        <NoticesSidebarOutline
          organizationId={orgId}
          privacyCenterId={privacyCenterId}
          noticePublicId={draftNotice?.publicId}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          selectedAnchor={selectedStateSectionAnchor}
          setSelectedAnchor={setSelectedStateSectionAnchor}
          stateSections={stateSections}
          showFinancialIncentives={hasFinancialIncentiveSection && offersFinancialIncentives}
          showRetentionPolicy={hasCustomRetentionPolicySection}
          showBiometricIdentifiers={hasBiometricIdentifiersSection && collectsConsumerBiometricData}
          showFinancialNoticeSetupBadge={
            hasFinancialIncentiveSection && !isFinancialIncentiveSectionFinished
          }
          showRetentionPolicySetupBadge={
            hasCustomRetentionPolicySection && !isRetentionPolicySectionFinished
          }
          showBiometricIdentifiersSetupBadge={
            hasBiometricIdentifiersSection && !isBiometricIdentifiersSectionFinished
          }
          unlockedStateAnchors={stateSections
            .map((s) => s.anchor)
            .filter(
              (anchor) => draftNotice?.sections?.find((es) => es.anchor === anchor) !== undefined,
            )}
          hiddenStateAnchors={draftNotice?.hiddenStateSectionAnchors || []}
          restoreStateSection={restoreStateSection}
        />

        {currentStep === "financialIncentivesEditor" && (
          <FinancialIncentiveNoticeEditor
            draftNotice={draftNotice}
            refreshNotice={refreshNotice}
            organizationId={orgId}
            privacyCenterId={privacyCenterId}
          />
        )}

        {currentStep === "biometricIdentifiersEditor" && (
          <BiometricIdentifiersEditor
            draftNotice={draftNotice}
            refreshNotice={refreshNotice}
            organizationId={orgId}
            privacyCenterId={privacyCenterId}
          />
        )}

        {currentStep === "statesNoticesEditor" && (
          <div style={{ maxWidth: "764px" }}>
            <div className="tw-boundary">
              <ExpandablePanel
                title="What are US State Notices?"
                titleFontSize="lg"
                widthClass="w-full"
              >
                <>
                  <p className="text-gray-500 text-[14px]">
                    State privacy notices inform consumers about how your business collects, uses,
                    and shares their personal data. The notices provide transparency and empower
                    consumers with rights over their data. Each notice here is designed to reflect
                    the unique requirements of each state law in effect.{" "}
                  </p>
                  <p className="mt-4 text-[14px]">
                    <ExternalLink
                      href="https://www.truevault.com/learn/us-state-privacy-law-comparison"
                      heavy
                      icon
                    >
                      Learn more about US State Privacy Laws
                    </ExternalLink>
                  </p>
                </>
              </ExpandablePanel>

              {!isCaliforniaSectionVisible && (
                <Card variant="warning">
                  <p>
                    Your California state notice is disabled. If you were required to add a
                    “California Privacy Notice” link to your website, that link will no longer work.
                    Update the link to point to whatever content you are using in place of the
                    California disclosure.
                    <br />
                    <br />
                  </p>
                  <p>
                    In addition, if your business is required to post a Notice of Financial
                    Incentive, that link will not work because that notice is part of the California
                    disclosure that is disabled. You should have the link point to whatever content
                    you are using in place of the California incentive notice.
                  </p>
                </Card>
              )}

              {offersFinancialIncentives &&
                !isFinancialIncentiveSectionFinished &&
                isCaliforniaSectionVisible && (
                  <div className="mt-8 bg-neutral-0 rounded-[8px] p-6 border border-solid border-neutral-300 flex items-center justify-center">
                    <div className="mr-4">
                      <img src="/assets/images/stay_compliant/privacy_center/savings-piggy-bank.svg" />
                    </div>
                    <Button
                      color="tertiary"
                      onClick={() => setCurrentStep("financialIncentivesEditor")}
                    >
                      Add Notice of Financial Incentive
                    </Button>
                  </div>
                )}
              {collectsConsumerBiometricData &&
                !isBiometricIdentifiersSectionFinished &&
                isColoradoSectionVisible && (
                  <div className="mt-8 bg-neutral-0 rounded-[8px] p-6 border border-solid border-neutral-300 flex items-center justify-center">
                    <div className="mr-4">
                      <img src="/assets/images/stay_compliant/privacy_center/person-add-document.svg" />
                    </div>
                    <Button
                      color="tertiary"
                      onClick={() => setCurrentStep("biometricIdentifiersEditor")}
                    >
                      Add Policy on Biometric Identifiers
                    </Button>
                  </div>
                )}
            </div>

            {/* Important: make sure section editors do not have any tw-boundary ancestors in the DOM */}
            <div>
              <Form
                onSubmit={() => {}}
                render={(_formRenderProps) => (
                  <div>
                    {stateSections
                      // sort first on visibility, then on name
                      .sort((a, b) => {
                        const aHidden = draftNotice?.hiddenStateSectionAnchors?.includes(a.anchor);
                        const bHidden = draftNotice?.hiddenStateSectionAnchors?.includes(b.anchor);

                        if (aHidden && !bHidden) return 1;
                        if (!aHidden && bHidden) return -1;

                        return a.anchor.localeCompare(b.anchor);
                      })
                      .map((section) => {
                        const editableStateSection = draftNotice?.sections?.find(
                          (s) => s.anchor === section.anchor,
                        );

                        // Use a key that is based on the hidden and editable state of the section
                        // This works around an issue where the Redactor X editor doesn't become editable without the entire component re-rendering, so using a key that changes when the section becomes editable forces the component to re-render
                        let isVisible = !(draftNotice?.hiddenStateSectionAnchors || []).includes(
                          section.anchor,
                        );
                        let key = `${section.anchor}-${
                          editableStateSection ? "editable" : "locked"
                        }-${isVisible}`;
                        return (
                          <USStateNoticeEditor
                            key={key}
                            organizationId={orgId}
                            privacyCenterId={privacyCenterId}
                            noticeId={draftNotice.id}
                            noticePublicId={draftNotice.publicId}
                            editableStateSection={editableStateSection}
                            defaultStateSection={section}
                            refreshNotice={refreshNotice}
                            setIsSaved={setIsSaved}
                            setSaving={setIsSaving}
                            hiddenStateSectionAnchors={draftNotice?.hiddenStateSectionAnchors || []}
                            onUnlock={async () => {
                              await createEditableStateSection({
                                organizationId: orgId,
                                privacyCenterId: privacyCenterId,
                                noticeId: draftNotice.publicId,
                                stateSectionAnchor: section.anchor,
                              });
                              await refreshNotice();
                            }}
                            onLock={async () => {
                              await restoreStateSection(section.anchor, section.name);
                            }}
                            onVisibilityToggle={async () => {
                              await updateStateSectionVisibility({
                                organizationId: orgId,
                                privacyCenterId: privacyCenterId,
                                noticeId: draftNotice.publicId,
                                stateSectionAnchor: section.anchor,
                              });
                              await refreshNotice();
                            }}
                          />
                        );
                      })}
                  </div>
                )}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NoticesSidebarOutline({
  organizationId,
  privacyCenterId,
  noticePublicId,
  currentStep,
  setCurrentStep,
  selectedAnchor,
  setSelectedAnchor,
  stateSections,
  showFinancialIncentives,
  showBiometricIdentifiers,
  showFinancialNoticeSetupBadge,
  showBiometricIdentifiersSetupBadge,
  unlockedStateAnchors,
  hiddenStateAnchors,
  restoreStateSection,
}: {
  organizationId: string;
  privacyCenterId: string;
  noticePublicId: string;
  currentStep: StateNoticesStep;
  setCurrentStep: (step: StateNoticesStep) => void;
  selectedAnchor: string | null;
  setSelectedAnchor: (anchor: string) => void;
  stateSections: PrivacyCenterPolicySectionDto[];
  showFinancialIncentives: boolean;
  showRetentionPolicy: boolean;
  showBiometricIdentifiers: boolean;
  showFinancialNoticeSetupBadge: boolean;
  showRetentionPolicySetupBadge: boolean;
  showBiometricIdentifiersSetupBadge: boolean;
  unlockedStateAnchors: string[];
  hiddenStateAnchors: string[];
  restoreStateSection: (stateAnchor: string, sectionName: string) => Promise<void>;
}) {
  const menuItem = (
    key: string,
    contents: string,
    isSelected: boolean,
    onClick: () => void,
    indented: boolean = false,
    badgeText?: string,
    icon?: React.ReactNode,
  ) => {
    return (
      <div
        key={key}
        className={`w-[256px] py-[9px] px-[12px] text-[12px] rounded-[8px] font-medium cursor-pointer ${
          isSelected ? "text-neutral-900 bg-neutral-100" : "text-neutral-600"
        } ${indented ? "ml-sm" : ""}`}
        style={{ marginRight: "30px" }}
        onClick={() => onClick && onClick()}
      >
        {badgeText && badgeText.length > 0 ? (
          <div className="flex justify-between items-center">
            <div>{contents}</div>
            <div className="bg-amber-100 text-amber-800 px-1.5 ml-2 rounded-[10px]">
              {badgeText}
            </div>
          </div>
        ) : icon ? (
          <div className="flex justify-between items-center">
            <div>{contents}</div>
            {icon}
          </div>
        ) : (
          contents
        )}
      </div>
    );
  };

  const backButton = (
    <div
      className="w-[256px] flex py-[9px] px-[12px] cursor-pointer items-center gap-sm"
      onClick={() => setCurrentStep("statesNoticesEditor")}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        style={{ height: "14px", width: "12px" }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
        />
      </svg>
      <div className="text-[12px] font-medium">Back to all state notices</div>
    </div>
  );

  const [showRestoreWarningModal, setShowRestoreWarningModal] = useState(false);
  const [stateSectionToRestore, setStateSectionToRestore] = useState<{
    sectionName: string;
    stateAnchor: string;
  } | null>(null);

  if (currentStep === "financialIncentivesEditor") {
    return (
      <div className="tw-boundary">
        {backButton}
        {menuItem(undefined, "Notice of Financial Incentive", true, () => {})}
      </div>
    );
  }
  if (currentStep === "biometricIdentifiersEditor") {
    return (
      <div className="tw-boundary">
        {backButton}
        {menuItem(undefined, "Policy on Biometric Identifiers", true, () => {})}
      </div>
    );
  }

  return (
    <div className="tw-boundary" style={{ minWidth: "281px" }}>
      <div className="text-neutral-900 text-[14px] font-medium">Notices Outline</div>
      <div className="mt-3">
        {stateSections
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((section) => {
            if (hiddenStateAnchors.includes(section.anchor)) return null;

            const isSelected = section.anchor === selectedAnchor;
            const sectionOutlineTitle = section.name;

            let menuIcon = unlockedStateAnchors.includes(section.anchor) ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();

                  setStateSectionToRestore({
                    sectionName: section.name,
                    stateAnchor: section.anchor,
                  });
                  setShowRestoreWarningModal(true);
                }}
              >
                <ToolTip text={`Restore TrueVault's locked notice.`} width="250px">
                  <LockOpenIcon className="h-3 w-3" />
                </ToolTip>
              </div>
            ) : undefined;

            if (section.anchor === "california-privacy-notice") {
              return (
                <>
                  {menuItem(
                    section.anchor,
                    sectionOutlineTitle,
                    isSelected,
                    () => setSelectedAnchor(section.anchor),
                    /* indented = */ false,
                    /* badgeText = */ undefined,
                    menuIcon,
                  )}
                  {showFinancialIncentives &&
                    menuItem(
                      "financialIncentive",
                      "Notice of Financial Incentive",
                      false,
                      () => setCurrentStep("financialIncentivesEditor"),
                      true,
                      showFinancialNoticeSetupBadge ? "Set up" : undefined,
                    )}
                </>
              );
            }

            if (section.anchor === "colorado-privacy-notice") {
              return (
                <>
                  {menuItem(
                    section.anchor,
                    sectionOutlineTitle,
                    isSelected,
                    () => setSelectedAnchor(section.anchor),
                    /* indented = */ false,
                    /* badgeText = */ undefined,
                    menuIcon,
                  )}
                  {showBiometricIdentifiers &&
                    menuItem(
                      "biometricIdentifiers",
                      "Policy on Biometric Identifiers",
                      false,
                      () => setCurrentStep("biometricIdentifiersEditor"),
                      true,
                      showBiometricIdentifiersSetupBadge ? "Set up" : undefined,
                    )}
                </>
              );
            }

            return menuItem(
              section.anchor,
              sectionOutlineTitle,
              isSelected,
              () => {
                setSelectedAnchor(section.anchor);
                let sectionDiv = document.getElementById(`section-${section.anchor}`);
                if (sectionDiv) {
                  sectionDiv.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              },
              /* indented = */ false,
              /* badgeText = */ undefined,
              menuIcon,
            );
          })}
      </div>
      {hiddenStateAnchors?.length > 0 && (
        <>
          <hr className="mt-6 text-neutral-200" />
          <div className="text-neutral-900 text-[14px] font-medium mt-6">Hidden Notices</div>
          <div className="mt-3">
            {hiddenStateAnchors
              .sort((a, b) => a.localeCompare(b))
              .map((anchor) => {
                const section = stateSections.find((s) => s.anchor === anchor);
                if (!section) return null;

                return menuItem(
                  section.anchor,
                  section.name,
                  false,
                  () => {
                    setSelectedAnchor(section.anchor);
                    let sectionDiv = document.getElementById(`section-${section.anchor}`);
                    if (sectionDiv) {
                      sectionDiv.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                  },
                  /* indented = */ false,
                  /* badgeText = */ undefined,
                  <EyeSlashIcon className="h-3 w-3" />,
                );
              })}
          </div>
        </>
      )}
      {showRestoreWarningModal && (
        <RestoreStateSectionWarningModal
          closeModal={() => setShowRestoreWarningModal(false)}
          restoreFn={async () => {
            await downloadStateSectionContent(
              organizationId,
              privacyCenterId,
              noticePublicId,
              stateSectionToRestore!.stateAnchor,
            );
            await restoreStateSection(
              stateSectionToRestore!.stateAnchor,
              stateSectionToRestore!.sectionName,
            );
            setShowRestoreWarningModal(false);
            setStateSectionToRestore(null);
          }}
        />
      )}
    </div>
  );
}
